import { createFileRoute, redirect } from "@tanstack/react-router";
import { ContentHero, DocumentCardLink } from "@/components/blocks/editorial";
import MarkdownContent from "@/components/blocks/MarkdownContent";
import {
	getAgentContentUrls,
	getAgentReadableRedirectPath,
	getCanonicalPageUrl,
} from "@/lib/ai-content";
import { useContentReadTracking } from "@/lib/analytics";
import { content } from "@/lib/content";
import { renderContentMarkdown } from "@/lib/content-markdown";
import { getTranslator } from "@/lib/i18n";
import { buildPath, resolveLocaleForPath } from "@/lib/i18n/locale";
import { useI18n } from "@/lib/i18n/use-i18n";
import { requirePublicContent } from "@/lib/public-content";
import {
	buildArticleStructuredData,
	buildBreadcrumbStructuredData,
	buildSeo,
	stripHtml,
	toExcerpt,
} from "@/lib/seo";

export const Route = createFileRoute("/{-$locale}/docs/$slug")({
	component: DocsDetailPage,
	loader: async ({ params, location, serverContext }) => {
		const locale = resolveLocaleForPath(location.pathname, serverContext);
		const agentReadableRedirectPath = getAgentReadableRedirectPath(
			location.pathname,
			locale,
		);
		if (agentReadableRedirectPath) {
			throw redirect({
				href: buildPath(
					agentReadableRedirectPath,
					location.searchStr,
					location.hash,
				),
			});
		}

		const [result, docs] = await Promise.all([
			content.getDoc({ data: { slug: params.slug, locale } }),
			content.getDocs({ data: { locale } }),
		]);

		const { content: doc, availableLocales } = requirePublicContent(
			result,
			`/docs/${params.slug}`,
			location.searchStr,
			location.hash,
		);

		const contentHtml = renderContentMarkdown(doc.content, doc.media);
		return { doc, contentHtml, docs, locale, availableLocales };
	},
	head: ({ loaderData }) => {
		if (!loaderData) return {};
		const t = getTranslator(loaderData.locale);
		const descriptionFallback = t((t) => t.docs.heroDescription);
		const contentText = stripHtml(loaderData.contentHtml);
		const description = contentText
			? toExcerpt(contentText)
			: descriptionFallback;
		const path = `/docs/${loaderData.doc.slug}`;

		return buildSeo({
			availableLocales: loaderData.availableLocales,
			title: loaderData.doc.title,
			description,
			path,
			locale: loaderData.locale,
			type: "article",
			agentReadable: getAgentContentUrls(path, loaderData.locale),
			structuredData: [
				buildArticleStructuredData({
					title: loaderData.doc.title,
					description,
					path,
					locale: loaderData.locale,
					publishedAt: loaderData.doc.updatedAt ?? undefined,
					modifiedAt: loaderData.doc.updatedAt,
				}),
				buildBreadcrumbStructuredData([
					{
						name: t((t) => t.nav.home),
						url: getCanonicalPageUrl("/", loaderData.locale),
					},
					{
						name: t((t) => t.footer.docs),
						url: getCanonicalPageUrl("/docs", loaderData.locale),
					},
					{
						name: loaderData.doc.title,
						url: getCanonicalPageUrl(path, loaderData.locale),
					},
				]),
			],
		});
	},
});

function DocsDetailPage() {
	const { doc, contentHtml, docs } = Route.useLoaderData();
	const { t, locale, localeParam } = useI18n();
	const localeParams: Record<string, string> = localeParam
		? { locale: localeParam }
		: {};
	const otherDocs = docs.filter((item) => item.slug !== doc.slug);

	useContentReadTracking({
		contentType: "doc",
		slug: doc.slug,
		title: doc.title,
		locale,
	});

	return (
		<>
			<ContentHero label={t((t) => t.docs.heroLabel)} title={doc.title}>
				{doc.updatedAtLabel ? (
					<p className="text-sm text-muted-foreground">
						{t((t) => t.docs.effectiveDate)}: {doc.updatedAtLabel}
					</p>
				) : null}
			</ContentHero>

			<section className="mx-auto w-full max-w-3xl px-6 pt-12 pb-20">
				<MarkdownContent contentHtml={contentHtml} />

				{otherDocs.length > 0 ? (
					<div className="mt-16 grid gap-4 md:grid-cols-2">
						{otherDocs.map((item) => (
							<DocumentCardLink
								key={item.slug}
								doc={item}
								localeParams={localeParams}
								variant="compact"
							/>
						))}
					</div>
				) : null}
			</section>
		</>
	);
}
